"use client";

import type { useTranslations } from "@i18n/react";
import type { Collection } from "@packages/sdk";
import { differenceInCalendarDays, differenceInHours } from "date-fns";
import type { ReactNode } from "react";

import { Countdown } from "../components/blocks/primitives/Countdown";
import { formatCompactNumber } from "./numberFormat";

export const getChallengeStatus = (
  startsAt: Collection["available_at"],
  endsAt: Collection["ends_at"],
): "pending" | "live" | "ended" => {
  const now = new Date();
  const startsAtDate = new Date(startsAt);
  const endsAtDate = new Date(endsAt);

  if (now < startsAtDate) {
    return "pending";
  }
  if (now > endsAtDate) {
    return "ended";
  }
  return "live";
};

export const getDatesLabel = (
  startsAt: Collection["available_at"],
  endsAt: Collection["ends_at"],
  t: ReturnType<typeof useTranslations>,
): ReactNode | null => {
  const now = new Date();
  const endsAtDate = new Date(endsAt);
  const challengeStatus = getChallengeStatus(startsAt, endsAt);

  if (challengeStatus === "ended") {
    return null;
  }

  if (challengeStatus === "live") {
    const dayDiff = differenceInCalendarDays(endsAtDate, now);
    const hourDiff = differenceInHours(endsAtDate, now);

    if (dayDiff <= 0) {
      return t("challenge_tile_hours_left_label", {
        challenge_tile_hours_left_label: hourDiff,
      });
    }
    return t("challenge_tile_days_left_label", {
      challenge_tile_days_left_label: dayDiff,
    });
  }

  if (challengeStatus === "pending") {
    return <Countdown format="default" targetDate={startsAt} />;
  }

  return null;
};

export const getUsersCountLabel = (
  collection: Pick<
    Collection,
    "users_count" | "available_at" | "ends_at" | "place_in_line"
  >,
  t: ReturnType<typeof useTranslations>,
): string | null => {
  if (!collection.users_count) {
    return null;
  }

  let usersCount = collection.users_count;
  let placeInLine = collection.place_in_line;
  // This is a workaround to deal with the fact that it takes ~10-30 seconds for the users_count to be updated on the BE, whilst place_in_line is updated immediately
  if (collection.place_in_line > collection.users_count) {
    usersCount = collection.place_in_line;
  }
  if (collection.users_count > collection.place_in_line) {
    placeInLine = collection.users_count;
  }

  const challengeStatus = getChallengeStatus(
    collection.available_at,
    collection.ends_at,
  );

  if (challengeStatus === "ended") {
    return t("challenge_tile_join_count", {
      "0": formatCompactNumber(usersCount),
    });
  }

  // Show place in line if available and under 10,000
  if (collection.place_in_line && placeInLine < 10_000) {
    const props = {
      "0": formatCompactNumber(placeInLine),
      "1": formatCompactNumber(usersCount),
    };

    if (challengeStatus === "live") {
      return t("challenge_tile_of_praying", props);
    } else if (challengeStatus === "pending") {
      return t("challenge_tile_of_joined", props);
    }
  }

  // Default: just show total users count
  return t("challenge_tile_praying", {
    "0": formatCompactNumber(usersCount),
  });
};
